import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Link,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
} from '@mui/material';

import { Network, SocialAccount } from 'api';
import { SocialIcon } from 'components';
import { getSocialAccountName } from 'helpers';
import { pxToRem } from 'theme';

const styles = {
  button: {
    justifyContent: 'flex-end',
  },
  secondaryAction: {
    right: 0,
  },
  secondary: {
    fontSize: pxToRem(12),
  },
} as const;

interface Props {
  account: SocialAccount;
}

const ProfileItem: FC<Props> = ({ account }) => {
  const { t } = useTranslation();
  const { identifier, reach, provider, profile_url } = account;

  const accountName = getSocialAccountName(account);
  const isTwitter = provider === Network.Twitter;
  const isLinkedIn = provider === Network.Linkedin;

  function renderFollowers() {
    return (
      <Typography color="textSecondary" sx={styles.secondary}>
        {t('components:xFollower', { count: reach })}
      </Typography>
    );
  }

  function renderConnections() {
    return (
      <Typography color="textSecondary" sx={styles.secondary}>
        {t('components:xConnection', { count: reach })}
      </Typography>
    );
  }

  function renderFollow() {
    return (
      <Link
        target="_blank"
        href={`https://twitter.com/intent/follow?screen_name=${identifier}`}
        sx={styles.button}
        underline="hover"
      >
        {t('common:follow')}
      </Link>
    );
  }

  function renderConnect() {
    return (
      <Link target="_blank" href={profile_url} sx={styles.button} underline="hover">
        {t('common:connect')}
      </Link>
    );
  }

  return (
    <ListItem disableGutters>
      <ListItemIcon>
        <SocialIcon size={24} provider={provider} />
      </ListItemIcon>
      <ListItemText
        disableTypography
        primary={
          <Link
            noWrap
            variant="body2"
            display="block"
            target="_blank"
            href={profile_url}
            underline="hover"
          >
            {accountName}
          </Link>
        }
        secondary={
          <Fragment>
            {isTwitter && renderFollowers()}
            {isLinkedIn && renderConnections()}
          </Fragment>
        }
      />
      <ListItemSecondaryAction sx={styles.secondaryAction}>
        {isTwitter && renderFollow()}
        {isLinkedIn && renderConnect()}
      </ListItemSecondaryAction>
    </ListItem>
  );
};

export default ProfileItem;
